'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  HUBSPOT_ERROR_CODES,
  GENERIC_HUBSPOT_ERROR_MESSAGE,
  type HubSpotErrorCode,
} from '@workspace/constants/contact-form.constant';
import { pushFormEvent } from '@workspace/lib/gtm-tracking';
import { contactFormSchema } from '@workspace/schema/contact-form.schema';
import { createNewsletterSubmissionData } from '@workspace/lib/hubspot-utils';
import { NEWSLETTER_MESSAGES } from '@workspace/constants/newsletter.constant';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { NewsletterSubmitArgs } from './newsletter.types';

const schema = z.object({
  email: contactFormSchema.shape.email,
  honeypot: z.string().optional(),
});

export function useNewsletter() {
  const [showSuccess, setShowSuccess] = useState(false);
  const GENERIC_ERROR = GENERIC_HUBSPOT_ERROR_MESSAGE;
  const [loading, setLoading] = useState(false);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema as any),
    defaultValues: { email: '' },
  });

  const submit = async (args: NewsletterSubmitArgs): Promise<any> => {
    try {
      const response = await fetch('/api/hubspot/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...args, submittedAt: Date.now() }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const code = (data?.code as HubSpotErrorCode) ?? HUBSPOT_ERROR_CODES.UNKNOWN_ERROR;
        throw new Error(code);
      }

      return {
        success: true,
        message: data?.message ?? 'Newsletter subscribed successfully',
      };
    } catch {
      return { success: false, error: GENERIC_ERROR };
    }
  };

  const handleInvalidSubmit = (): void => {
    pushFormEvent('form_submission_error', {
      form_name: 'newsletter_form',
      form_location: typeof window !== 'undefined' ? window.location.pathname : '',
      error_type: 'validation_error',
    });
  };

  const onSubmit = async (values: z.infer<typeof schema>) => {
    if (loading) return;
    setLoading(true);
    setShowSuccess(false);
    try {
      if (values.honeypot) {
        return;
      }
      const payload = createNewsletterSubmissionData([{ name: 'email', value: values.email }], false);
      const res = await submit(payload);

      if (res.success) {
        pushFormEvent('form_submission', {
          form_name: 'newsletter_form',
          form_location: typeof window !== 'undefined' ? window.location.pathname : '',
        });
        form.reset();
        setShowSuccess(true);
      } else {
        pushFormEvent('form_submission_error', {
          form_name: 'newsletter_form',
          form_location: typeof window !== 'undefined' ? window.location.pathname : '',
          error_type: 'api_error',
        });
        form.setError('email', {
          message: res.error || NEWSLETTER_MESSAGES.ERROR,
        });
      }
    } finally {
      // ensure loading false always at end
      setLoading(false);
    }
  };

  return {
    form,
    onSubmit,
    handleInvalidSubmit,
    showSuccess,
    loading,
  };
}
